// Gemeinsame Bedienung der Menü-Panels der Verkäufe (Filter und Gruppierung, Kundenwünsche 12.10.2026; das Zeitraum-
// Feld nutzt die Fokusführung): ein v-menu (ref "menu", Zustand open) mit einem Panel (ref "panel", role="dialog",
// tabindex="-1") unter einem Feldknopf im Wurzelelement.
// - Höhe: bis 12 px über den unteren Rand des sichtbaren Fensterbereichs (Abstand 4 zum Knopf, Kante des Menüs 2 × 1),
//   auf dem Handy höchstens 70 % der Fensterhöhe, mindestens 240; das Panel trägt sie über panelStyle. Ändert sich das
//   Fenster bei offenem Panel (Bildschirmtastatur auf dem Handy, Drehen), wird neu gemessen und das fokussierte Feld
//   bleibt im Bild (seit der Prüfung vom 12.10.2026). Mindesthöhe: panelMinHeight der Komponente (null: keine),
//   höchstens die gemessene Höhe.
// - Lage: alignRightFor wählt beim Öffnen die Seite, auf der das Menü am Knopf ins Fenster passt (menuLeft für das
//   v-menu); die Komponente setzt dafür rightAligned in ihrer Methode place().
// - Fokus: beim Öffnen auf das erste Bedienelement (focusWhenShown), Tab und Umschalt+Tab bleiben im Panel (trapTab),
//   auch nach einem Klick auf eine freie Stelle (das Panel nimmt den Fokus dann selbst, Esc wirkt weiter); Esc schließt
//   VMenu selbst und gibt den Fokus an den Feldknopf zurück.
// - updateMenu(): Lage neu berechnen, wenn sich die Größe des offenen Panels ändert; relayout(): zusätzlich Seite und
//   Höhe neu wählen (z. B. wenn der Knopf in der Werkzeugleiste umbricht).
// - v-scroll-hint: Scrollschatten oben und unten (Klassen scroll--oben, scroll--unten), solange dort Inhalt verdeckt
//   ist; die Komponenten zeichnen ihn in ihren Stilen (Token --sh-scroll wie die Navigationsliste).

// Abstand der Menüs zum Fensterrand
const MARGIN = 12;

function viewportWidth() {
  return document.documentElement.clientWidth || window.innerWidth;
}

// unterer Rand des sichtbaren Bereichs: mit Bildschirmtastatur kleiner als das Fenster (visualViewport)
function viewportBottom() {
  const vv = window.visualViewport;
  return vv ? Math.min(window.innerHeight, vv.offsetTop + vv.height) : window.innerHeight;
}

// passt ein Menü dieser Breite (samt Kante) rechtsbündig bzw. linksbündig an den Knopf, mit 12 px zum Fensterrand?
export function fitsAt(button, width, right) {
  if (!button) {
    return false;
  }
  const rect = button.getBoundingClientRect();
  return right ? rect.right - width >= MARGIN : rect.left + width <= viewportWidth() - MARGIN;
}

// Seite eines Menüs am Knopf: die bevorzugte (rechtsbündig = true), wenn es dort passt, sonst die andere; passt es auf
// keiner, bleibt die bevorzugte (VMenu schiebt das Menü dann ins Fenster)
export function alignRightFor(button, width, preferRight) {
  if (fitsAt(button, width, preferRight)) {
    return preferRight;
  }
  return fitsAt(button, width, !preferRight) ? !preferRight : preferRight;
}

// Scrollschatten: Klassen am Scrollbereich nach Lage des Inhalts, nachgeführt bei Scrollen, Größenänderung und
// Neuzeichnen der Komponente
function updateScrollHint(el) {
  const more = el.scrollHeight - el.clientHeight > 1;
  el.classList.toggle('scroll--oben', more && el.scrollTop > 0);
  el.classList.toggle('scroll--unten', more && el.scrollTop + el.clientHeight < el.scrollHeight - 1);
}

const scrollHint = {
  inserted(el) {
    const update = () => updateScrollHint(el);
    const state = { update, observer: null };
    el.addEventListener('scroll', update, { passive: true });
    if (typeof window.ResizeObserver === 'function') {
      state.observer = new window.ResizeObserver(update);
      state.observer.observe(el);
      Array.prototype.forEach.call(el.children, (child) => state.observer.observe(child));
    }
    el.scrollHintState = state;
    update();
  },
  componentUpdated(el) {
    const state = el.scrollHintState;
    if (!state) {
      return;
    }
    // Kinder neu beobachten (aufgeklappte Liste, andere Liste in der Spalte; entfernte nicht weiter festhalten)
    if (state.observer) {
      state.observer.disconnect();
      state.observer.observe(el);
      Array.prototype.forEach.call(el.children, (child) => state.observer.observe(child));
    }
    state.update();
  },
  unbind(el) {
    const state = el.scrollHintState;
    if (!state) {
      return;
    }
    el.removeEventListener('scroll', state.update);
    if (state.observer) {
      state.observer.disconnect();
    }
    el.scrollHintState = null;
  }
};

export default {
  directives: { scrollHint },
  data() {
    return {
      open: false,
      maxHeight: null,
      // Seite am Knopf nach place() (null: wie alignRight)
      rightAligned: null
    };
  },
  computed: {
    touch() {
      return !!(this.$dichte && this.$dichte.touch);
    },
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    // Komponenten mit Mindesthöhe überschreiben das (Filter, Kundenwunsch 13.10.2026)
    panelMinHeight() {
      return null;
    },
    panelStyle() {
      if (!this.maxHeight) {
        return null;
      }
      const style = { maxHeight: this.maxHeight + 'px' };
      if (this.panelMinHeight) {
        style.minHeight = Math.min(this.panelMinHeight, this.maxHeight) + 'px';
      }
      return style;
    },
    // v-menu left: Menü rechtsbündig zum Knopf
    menuLeft() {
      return this.rightAligned === null ? !!this.alignRight : this.rightAligned;
    }
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.listenViewport();
      } else {
        this.unlistenViewport();
      }
    }
  },
  created() {
    // nicht reaktiv: Beobachter der Fenstergröße bei offenem Panel
    this.viewportHandler = null;
    this.viewportFrame = null;
  },
  beforeDestroy() {
    this.unlistenViewport();
  },
  methods: {
    updateMenu() {
      if (this.open && this.$refs.menu) {
        this.$refs.menu.updateDimensions();
      }
    },
    activator() {
      return this.$el ? this.$el.querySelector('.app-btn') : null;
    },
    measure() {
      const rect = this.$el.getBoundingClientRect();
      const height = viewportBottom();
      const below = Math.floor(height - rect.bottom - 18);
      const limit = this.handy ? Math.floor(height * 0.7) : below;
      this.maxHeight = Math.max(240, Math.min(below, limit));
    },
    // Seite und Höhe neu wählen und die Lage neu berechnen (offenes Panel)
    relayout() {
      if (!this.open) {
        return;
      }
      if (typeof this.place === 'function') {
        this.place();
      }
      this.measure();
      this.$nextTick(() => {
        this.updateMenu();
        this.revealFocus();
      });
    },
    listenViewport() {
      if (this.viewportHandler) {
        return;
      }
      this.viewportHandler = () => {
        window.cancelAnimationFrame(this.viewportFrame);
        this.viewportFrame = window.requestAnimationFrame(this.relayout);
      };
      window.addEventListener('resize', this.viewportHandler);
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.viewportHandler);
      }
    },
    unlistenViewport() {
      if (!this.viewportHandler) {
        return;
      }
      window.removeEventListener('resize', this.viewportHandler);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.viewportHandler);
      }
      window.cancelAnimationFrame(this.viewportFrame);
      this.viewportHandler = null;
    },
    // fokussiertes Feld im Panel sichtbar halten (nur im eigenen Scrollbereich, die Seite scrollt nicht)
    revealFocus() {
      const panel = this.$refs.panel;
      const el = document.activeElement;
      if (!panel || !el || el === panel || !panel.contains(el)) {
        return;
      }
      let box = el.parentElement;
      while (box && box !== panel && !(box.scrollHeight > box.clientHeight + 1 && /(auto|scroll)/.test(getComputedStyle(box).overflowY))) {
        box = box.parentElement;
      }
      if (!box || box === panel) {
        return;
      }
      const r = el.getBoundingClientRect();
      const b = box.getBoundingClientRect();
      if (r.bottom > b.bottom) {
        box.scrollTop += r.bottom - b.bottom + 8;
      } else if (r.top < b.top) {
        box.scrollTop -= b.top - r.top + 8;
      }
    },
    focusables() {
      const panel = this.$refs.panel;
      if (!panel) {
        return [];
      }
      return Array.from(panel.querySelectorAll('button, input')).filter(
        (el) => !el.disabled && el.offsetParent !== null
      );
    },
    focusFirst() {
      const first = this.focusables()[0];
      if (this.open && first) {
        first.focus();
      }
      return !!first;
    },
    // erst fokussierbar, wenn VMenu den Inhalt eingeblendet hat: einige Bilder lang erneut versuchen
    focusWhenShown(tries) {
      window.requestAnimationFrame(() => {
        if (this.open && !this.focusFirst() && tries > 0) {
          this.focusWhenShown(tries - 1);
        }
      });
    },
    // Fokus nach einem Umbau im Panel setzen: Vuetify verschiebt den Knopf einer neu entstandenen Auswahl (AppSelect)
    // erst einen Takt nach dem Einhängen und nimmt ihm dabei den Fokus, deshalb erst im nächsten Bild; gelingt es nicht
    // (Element fehlt), der erste Knopf des Panels
    focusLater(fn) {
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          if (!this.open) {
            return;
          }
          if (!fn() && !this.panelHasFocus()) {
            this.focusFirst();
          }
        });
      });
    },
    panelHasFocus() {
      const panel = this.$refs.panel;
      return !!panel && panel.contains(document.activeElement);
    },
    // Tab und Umschalt+Tab bleiben im Panel; nach einem Klick auf eine freie Stelle liegt der Fokus am Panel selbst
    trapTab(e) {
      const items = this.focusables();
      if (!items.length) {
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (active === this.$refs.panel) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
};
